import imageCompression from 'browser-image-compression';

const MAX_LONG_EDGE = 2400;
const SKIP_IF_BYTES_UNDER = 2_000_000; // 2MB 以下直接原图上传,保留手写笔画细节
const SKIP_IF_LONG_EDGE_UNDER = 2000;

export type ImageMeta = {
  width: number;
  height: number;
  original_name: string;
  original_size: number;
  compressed: boolean;
};

export type PreparedImage = { file: File; meta: ImageMeta };

async function getDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

export async function prepareImage(file: File): Promise<PreparedImage> {
  const dims = await getDimensions(file);
  const originalSize = file.size;
  const originalName = file.name || 'image';
  const longEdge = dims ? Math.max(dims.width, dims.height) : MAX_LONG_EDGE;

  const skip = originalSize <= SKIP_IF_BYTES_UNDER && longEdge <= SKIP_IF_LONG_EDGE_UNDER;

  if (skip) {
    return {
      file,
      meta: {
        width: dims?.width ?? 0,
        height: dims?.height ?? 0,
        original_name: originalName,
        original_size: originalSize,
        compressed: false,
      },
    };
  }

  const compressed = await imageCompression(file, {
    maxSizeMB: 3,
    maxWidthOrHeight: MAX_LONG_EDGE,
    useWebWorker: true,
    initialQuality: 0.92,
    fileType: 'image/jpeg',
  });
  const finalDims = (await getDimensions(compressed)) ?? dims ?? { width: 0, height: 0 };

  return {
    file: compressed,
    meta: {
      width: finalDims.width,
      height: finalDims.height,
      original_name: originalName,
      original_size: originalSize,
      compressed: true,
    },
  };
}
